import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["lang"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Text, FullWidthContainer, JukeboxProvider } from '@xceedsrl/jukebox';
import { useFormik } from 'formik';
import TicketBuyer from '../components/TicketBuyer';
import TicketHolders from '../components/TicketHolders';
import SectionTitle from '../components/SectionTitle';
import { getDefaultFormFields } from '../config/constants';
import { makeValidationSchema, mergeFormFields, getFormState, convertBookingData } from '../utils/forms';
import { I18nProvider, useTranslation } from '../i18n/I18nProvider';
import { GlobalStyle } from '../components/GlobalStyle';
var Form = 'form';
var FreePurchase = forwardRef(function (_ref, ref) {
  var initialValues = _ref.initialValues,
    buyerFields = _ref.buyerFields,
    holderFields = _ref.holderFields,
    attendesForPurchase = _ref.attendesForPurchase,
    onValid = _ref.onValid,
    disabledBuyerFields = _ref.disabledBuyerFields,
    customTitleColor = _ref.customTitleColor,
    _onSubmit = _ref.onSubmit,
    participants = _ref.participants;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var hasCustomFields = holderFields.length > 0;
  var defaultBuyerFields = useMemo(function () {
    return getDefaultFormFields(t);
  }, [t]);
  var normalizedBuyerFields = useMemo(function () {
    return mergeFormFields(defaultBuyerFields, buyerFields, t);
  }, [defaultBuyerFields, buyerFields, t]);
  var translatedHolderField = holderFields.map(function (field) {
    return _objectSpread(_objectSpread({}, field), {}, {
      name: t(field.slug) === field.slug ? field.name : t(field.slug)
    });
  });
  var formState = useMemo(function () {
    return getFormState({
      holderFields: translatedHolderField,
      attendesForPurchase: attendesForPurchase,
      initialValues: initialValues,
      buyerFields: normalizedBuyerFields,
      participants: participants
    });
  }, [attendesForPurchase, normalizedBuyerFields, translatedHolderField]);
  var requiredFields = useMemo(function () {
    return Object.keys(formState).reduce(function (list, field) {
      var existingField = normalizedBuyerFields.find(function (el) {
        return el.slug === field;
      });
      list[field] = existingField ? existingField.isRequired : true;
      return list;
    }, {});
  }, [formState, normalizedBuyerFields]);
  var validationSchema = useMemo(function () {
    return makeValidationSchema(requiredFields, t);
  }, [requiredFields, t]);
  var formik = useFormik({
    initialValues: formState,
    validationSchema: validationSchema,
    onSubmit: function onSubmit(values) {
      var formattedValues = convertBookingData(values);
      _onSubmit(formattedValues);
    }
  });
  useEffect(function () {
    return void formik.validateForm();
  }, []);
  useEffect(function () {
    var isValid = formik.isValid;
    onValid && onValid(isValid, formik.values);
  }, [formik.isValid, formik.values]);
  return React.createElement(JukeboxProvider, null, React.createElement(FullWidthContainer, {
    id: "xceed-dropin-container"
  }, React.createElement(Box, null, React.createElement(Form, {
    onSubmit: formik.handleSubmit
  }, React.createElement(Box, {
    mb: 3
  }, React.createElement(SectionTitle, {
    color: customTitleColor
  }, t('ticketBuyer')), !!disabledBuyerFields.length && React.createElement(Text, null, t('ticketBuyerDisabledText')), React.createElement(TicketBuyer, {
    formik: formik,
    fields: normalizedBuyerFields,
    disabledBuyerFields: disabledBuyerFields
  })), hasCustomFields && React.createElement(TicketHolders, {
    holderFields: translatedHolderField,
    attendesForPurchase: attendesForPurchase,
    customTitleColor: customTitleColor,
    formik: formik
  }), React.createElement("button", {
    type: "submit",
    style: {
      display: 'none'
    },
    ref: ref
  })))), React.createElement(GlobalStyle, {
    namespace: "xceed-dropin-container"
  }));
});
FreePurchase.displayName = 'FreePurchase';
FreePurchase.defaultProps = {
  buyerFields: [],
  holderFields: [],
  onValid: function onValid() {},
  disabledBuyerFields: [],
  lang: PropTypes.string.isRequired,
  customTitleColor: ''
};
var ParticipantsForm = forwardRef(function (_ref2, ref) {
  var lang = _ref2.lang,
    props = _objectWithoutPropertiesLoose(_ref2, _excluded);
  return React.createElement(I18nProvider, {
    lang: lang
  }, React.createElement(FreePurchase, _extends({}, props, {
    ref: ref
  })));
});
export default React.memo(ParticipantsForm);